import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

import { GoToJobButton } from './goToJobButton';
import QueryResultsFooter from './queryResultsFooter';

const DBResultsTable = styled(Table)(function ({ theme }) {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    whiteSpace: 'nowrap',
    height: 'max-content',
    backgroundColor: isDarkMode ? '#505050' : '#EDEDED',
  };
});

const DBResultsRow = styled(TableRow)({
  height: '30px',
  maxHeight: '30px',
});

const DBResultsHeaderCell = styled('th')(function ({ theme }) {
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    textAlign: 'left',
    padding: '2px 15px',
    border: '1px solid #e2e2e2',
    backgroundColor: isDarkMode ? '#424242' : '#e4e4e4',
    fontWeight: 'normal',
  };
});

const DBResultsCell = styled(TableCell)({
  textAlign: 'left',
  border: '1px solid #e2e2e2',
  padding: '2px 15px',
});

export default function QueryResultsTable(props: {
  queryId?: string;
  headerColumns?: string[];
  data?: string[][];
  executionTime?: number;
  resultsLink?: string | null;
  loading: boolean;
  error?: { msg?: string; code?: number } | null;
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: isDarkMode ? '#505050' : '#EDEDED',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      {!props.loading && props.error && (
        <Typography variant='body1' color='error' className='p-[20px]'>
          <Stack direction='row' alignItems='start'>
            <i className='material-symbols-error-outline-rounded mr-[8px]'></i>
            <Stack direction='column' width='600px'>
              <Typography variant='body1' color='error' className='pb-[8px]'>
                Error Code: {props.error.code || ''}
              </Typography>
              <Typography variant='body1' color='error'>
                Message: {props.error.msg || ''}
              </Typography>
            </Stack>
          </Stack>
        </Typography>
      )}
      {!props.loading && props.data?.length === 0 && (
        <Stack
          className='h-full text-center'
          justifyContent='center'
          alignItems='center'
        >
          <Typography variant='body2'>No records found</Typography>
        </Stack>
      )}
      {!props.loading && props.data?.length && (
        <Stack className='h-full'>
          <Box className='flex flex-grow' sx={{ overflow: 'auto' }}>
            <DBResultsTable>
              {props.headerColumns && (
                <TableHead>
                  <DBResultsRow>
                    {props.headerColumns.map((header, i) => (
                      <DBResultsHeaderCell key={i}>
                        {header}
                      </DBResultsHeaderCell>
                    ))}
                  </DBResultsRow>
                </TableHead>
              )}

              <TableBody>
                {props.data?.map((row, i) => (
                  <DBResultsRow key={i}>
                    {row.map((cell, j) => (
                      <DBResultsCell key={j}>{cell}</DBResultsCell>
                    ))}
                  </DBResultsRow>
                ))}
              </TableBody>
            </DBResultsTable>
          </Box>
          <QueryResultsFooter {...props} />
        </Stack>
      )}
      {props.loading && <QueryResultsInProgress queryId={props.queryId} />}
    </Box>
  );
}

export const QueryResultsInProgress = (props: { queryId?: string }) => {
  return (
    <Stack
      gap='24px'
      className='h-full text-center'
      justifyContent='center'
      alignItems='center'
    >
      <Skeleton width='200px' />
      <Stack gap='12px'>
        <Typography width='420px' variant='body1'>
          Restoring records. You can wait here for the records or view them in
          the restore job when it’s done.
        </Typography>
        {props.queryId && (
          <GoToJobButton jobId={props.queryId} jobType='restore' />
        )}
      </Stack>
    </Stack>
  );
};
